import React, { useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, LinearProgress } from '@mui/material'
import { CRM_POLL, crmApi } from '../../../features/crm/crmApi'
import { Card, DetailsButton, PageHead, Pill, TableWrap, contentSx, pageSx } from '../shared/crmUi'
import { courseTitle, dictLabel, subjectTitle } from '../shared/format'
import { headStage } from '../shared/stages'
import HeadTrackInfo from './HeadTrackInfo'
import { EmptyRow, HeadSubjectSelect, TimeChip, useHeadSubject } from './HeadCrmNewPage'

const HeadCrmStudentsPage = () => {
    const { formatMessage, locale } = useIntl()
    const hs = useHeadSubject()
    const { data = [], isFetching } = crmApi.useGetHeadTracksQuery(
        { subjectId: hs.subjectId ?? 0, tab: 'ALL' },
        { skip: !hs.subjectId, pollingInterval: CRM_POLL },
    )
    const [open, setOpen] = useState<Record<number, boolean>>({})
    const rows = useMemo(() => [...data].sort((a, b) => ((a.headAt || '') < (b.headAt || '') ? 1 : -1)), [data])
    const subjName = hs.subject ? subjectTitle(hs.subject, locale) : ''

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead
                    title={`${formatMessage({ id: 'crmAllStudents' })}${subjName ? ` · ${subjName}` : ''}`}
                    sub={formatMessage({ id: 'crmHeadAllSub' })}
                    action={<HeadSubjectSelect subjects={hs.subjects} value={hs.subjectId} onChange={hs.setSubjectId} />}
                />
                {isFetching && <LinearProgress sx={{ marginBottom: 1 }} />}
                <Card pad={false}>
                    <TableWrap minWidth={900}>
                        <table>
                            <thead>
                                <tr>
                                    <th>{formatMessage({ id: 'crmColStudent' })}</th>
                                    <th>{formatMessage({ id: 'crmLanguage' })}</th>
                                    <th>{formatMessage({ id: 'crmMentor' })}</th>
                                    <th>{formatMessage({ id: 'crmCourse' })}</th>
                                    <th>{formatMessage({ id: 'crmGroup' })}</th>
                                    <th>{formatMessage({ id: 'crmColStage' })}</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {rows.map((t) => {
                                    const st = headStage(formatMessage, t)
                                    return (
                                        <React.Fragment key={t.id}>
                                            <tr>
                                                <td>
                                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                                                        <TimeChip iso={t.headAt} />
                                                        <b>{t.studentName}</b>
                                                    </Box>
                                                </td>
                                                <td>{dictLabel(formatMessage, 'Lang', t.language)}</td>
                                                <td>{t.mentorId ? <Pill tone="pink">{t.mentorName || '-'}</Pill> : '-'}</td>
                                                <td>{courseTitle(t, locale)}</td>
                                                <td>{t.groupTitle || '-'}</td>
                                                <td>
                                                    <Pill tone={st.tone}>{st.text}</Pill>
                                                </td>
                                                <td className="r">
                                                    <DetailsButton open={!!open[t.id]} onClick={() => setOpen((o) => ({ ...o, [t.id]: !o[t.id] }))} />
                                                </td>
                                            </tr>
                                            {open[t.id] && (
                                                <tr className="det">
                                                    <td colSpan={7}>
                                                        <HeadTrackInfo t={t} />
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    )
                                })}
                                {!rows.length && <EmptyRow cols={7} text={formatMessage({ id: 'crmNoStudents' })} />}
                            </tbody>
                        </table>
                    </TableWrap>
                </Card>
            </Box>
        </Box>
    )
}

export default HeadCrmStudentsPage
